import { EXPERIENCE } from '../../constants/experience';
import { IExperience } from '../../types/api';
import { IMarketplaceProductDetails } from '../../types/marketplace';
import { ICampaignItem } from '../../types/pyg';
import {
  ActionsEnum,
  IAction,
  IError,
  IInitState,
  IInitSuccessPayload,
  IMarketplaceHydrateItemSuccessPayload,
  ISetWhiteLabelingPayload,
  ISubmitPYGPayload,
} from '../../types/store';
import { formatSkuOptions } from '../../util/helpers';
import { addMarketplacePrefix, hydrateItemWithVariants } from '../../util/marketplace';

export const initialState: IInitState = {
  isLoading: false,
  error: null,
  org: null,
  whiteLabeling: null,
  sender: null,
  address: null,
  campaigns: null,
  items: null,
  isDigital: false,
  isFormSubmitted: false,
  password: null,
  send2FATo: null,
  code: null,
  shippingRules: null,
  experience: null,
  isEmbed: false,
  giftBudget: null,
  customScenarios: [],
};

const hydrateWithDetails = (state: IInitState, productId: string, details: IMarketplaceProductDetails): IInitState => {
  const prefixedId = addMarketplacePrefix(productId);
  const hydrate = (item: ICampaignItem) =>
    item.id === prefixedId
      ? hydrateItemWithVariants({ ...item, description: item.description || details.description }, details.variants)
      : item;

  return {
    ...state,
    campaigns:
      state.campaigns?.map((campaign) =>
        campaign.id === prefixedId ? { ...campaign, items: campaign.items.map(hydrate) } : campaign,
      ) ?? null,
    items: state.items?.map(hydrate) ?? null,
  };
};

const reducer = (state: IInitState = initialState, action: IAction): IInitState => {
  switch (action.type) {
    case ActionsEnum.GetFormDataRequest:
    case ActionsEnum.UpdateRequest:
      return { ...state, isLoading: true };
    case ActionsEnum.GetFormDataFailure: {
      const { [EXPERIENCE]: experience = null, ...error } = action.payload as IError &
        Pick<IInitSuccessPayload, typeof EXPERIENCE>;

      return { ...state, isLoading: false, error, experience };
    }
    case ActionsEnum.GetFormDataSuccess: {
      const {
        inventory_items,
        ds_customized_texts: whiteLabeling,
        pick_campaigns: campaigns,
        org_info: org,
        sender_info: sender,
        is_digital,
        receiver_info: address,
        shipping_rules: shippingRules,
        experience = null,
        gift_budget: giftBudget = null,
        custom_scenarios: customScenarios,
      } = action.payload as IInitSuccessPayload;

      const items = formatSkuOptions(inventory_items);

      return {
        ...state,
        isLoading: false,
        items,
        campaigns,
        address,
        org,
        sender,
        isDigital: !!is_digital,
        whiteLabeling,
        shippingRules,
        experience,
        giftBudget,
        customScenarios: (customScenarios ?? []).map(({ value }) => value).filter(Boolean),
      };
    }
    case ActionsEnum.UpdateSuccess:
    case ActionsEnum.UpdateFailure: {
      return { ...state, isLoading: false };
    }
    case ActionsEnum.SubmitPYG: {
      const { campaignId } = action.payload as ISubmitPYGPayload;
      const selectedCampaign = state.campaigns?.find(({ id }) => campaignId === id);
      const items = selectedCampaign ? formatSkuOptions(selectedCampaign.items) : undefined;

      return {
        ...state,
        ...(items ? { items } : {}),
        isDigital: !!selectedCampaign?.is_digital,
      };
    }
    case ActionsEnum.MarketplaceHydrateItemSuccess: {
      const { productId, details } = action.payload as IMarketplaceHydrateItemSuccessPayload;

      return hydrateWithDetails(state, productId, details);
    }
    case ActionsEnum.MarketplaceProductSuccess: {
      const details = action.payload as IMarketplaceProductDetails;

      return hydrateWithDetails(state, details.product_id, details);
    }
    case ActionsEnum.SetIsFormSubmitted: {
      return { ...state, isFormSubmitted: !!action.payload };
    }
    case ActionsEnum.SetPassword: {
      return { ...state, password: action.payload as string };
    }
    case ActionsEnum.Set2FATo: {
      return { ...state, send2FATo: action.payload as string };
    }
    case ActionsEnum.Set2FACode: {
      return { ...state, code: action.payload as string };
    }
    case ActionsEnum.SetWhiteLabeling: {
      const {
        ds_customized_texts: whiteLabeling,
        org_info: org,
        sender_info: sender,
      } = action.payload as ISetWhiteLabelingPayload;

      return {
        ...state,
        org,
        sender,
        whiteLabeling,
      };
    }
    case ActionsEnum.LoadIframeInitData:
      return { ...state, isEmbed: true };
    case ActionsEnum.SetIframeExperience:
      return {
        ...state,
        experience: {
          ...(state.experience || {}),
          ...(action.payload as Partial<IExperience>),
        } as IExperience,
      };
    default:
      return state;
  }
};

export default reducer;
